import React, { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Star, Eye, MessageCircle, FileText, Facebook, Twitter, Instagram, Linkedin, Youtube, Github, MoreHorizontal, Pencil, X } from 'lucide-react';
import CustomAlert from '../CustomAlert';
import { useUpdateProfile, useGetHellocianMetrics } from '../../services/user.service';
import { useAuthStore } from '../../store/authStore';

const ProfileDashboard = () => {
  const { user, setUser } = useAuthStore();
  const { data: metrics, isLoading: isMetricsLoading } = useGetHellocianMetrics();
  const updateProfile = useUpdateProfile();
  const queryClient = useQueryClient();
  
  const [socialLinks, setSocialLinks] = useState({
    facebook: '',
    twitter: '',
    instagram: '',
    linkedin: '',
    youtube: '',
    github: ''
  });

  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  // Populate social links from user data
  useEffect(() => {
    if (user && user.socials) {
      setSocialLinks(user.socials);
    }
  }, [user]);
  
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedSocial, setSelectedSocial] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editBio, setEditBio] = useState('');
  const [editSkills, setEditSkills] = useState('');
  
  const [animatedValues, setAnimatedValues] = useState({
    inbox: 0,
    order: 0,
    delivered: 0,
    completion: 0
  });

  // Initialize edit fields
  useEffect(() => {
    if (user) {
      setEditBio(user.bio || '');
      setEditSkills(user.skills?.join(', ') || '');
    }
    if (user?.socials) {
      setSocialLinks(user.socials);
    }
  }, [user]);

  // Animate progress bars based on metrics data
  useEffect(() => {
    if (metrics) {
      const timer = setTimeout(() => {
        setAnimatedValues({
          inbox: metrics.inbox || 0,
          order: metrics.order || 0,
          delivered: metrics.delivered || 0,
          completion: metrics.completion || 0
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [metrics]);

  const [urlError, setUrlError] = useState('');

  const validateUrl = (url) => {
    if (!url.trim()) {
      return 'URL is required';
    }
    
    // Basic URL validation
    try {
      const urlObj = new URL(url);
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        return 'URL must start with http:// or https://';
      }
      return '';
    } catch {
      // If URL constructor fails, try adding https://
      try {
        new URL(`https://${url}`);
        return '';
      } catch {
        return 'Please enter a valid URL';
      }
    }
  };

  const handleSaveProfile = async () => {
    try {
      const skillsArray = editSkills.split(',').map(s => s.trim()).filter(s => s !== '');
      const response = await updateProfile.mutateAsync({
        bio: editBio,
        skills: skillsArray
      });
      if (response.success && response.user) {
        setUser(response.user);
      }
      queryClient.invalidateQueries(['me']);
      setAlert({ show: true, type: 'success', message: 'Profile updated successfully!' });
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Save profile error:', error);
      setAlert({ show: true, type: 'error', message: error.response?.data?.error || 'Failed to update profile' });
    }
  };

  const handleSocialClick = (type) => {
    setSelectedSocial(type);
    setShowLinkModal(true);
    setUrlError('');
  };

  const handleSaveLink = () => {
    let url = document.getElementById('social-url').value.trim();
    
    if (!url) {
      setUrlError('URL is required');
      return;
    }

    // Auto-add https:// if no protocol is provided
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`;
    }

    const error = validateUrl(url);
    if (error) {
      setUrlError(error);
      return;
    }

    const updatedSocials = {
      ...socialLinks,
      [selectedSocial]: url
    };

    updateProfile.mutate({ socials: updatedSocials }, {
      onSuccess: (response) => {
        if (response.success && response.user) {
          setUser(response.user);
        }
        setSocialLinks(updatedSocials);
        queryClient.invalidateQueries(['me']);
        setShowLinkModal(false);
        setUrlError('');
        setAlert({ show: true, message: `${selectedSocial} link updated!`, type: 'success' });
      },
      onError: (err) => {
        console.error('Save link error:', err);
        setUrlError(err.response?.data?.error || 'Failed to update link');
      }
    });
  };

  const handleRemoveLink = () => {
    const updatedSocials = {
      ...socialLinks,
      [selectedSocial]: ''
    };

    updateProfile.mutate({ socials: updatedSocials }, {
      onSuccess: (response) => {
        if (response.success && response.user) {
          setUser(response.user);
        }
        setSocialLinks(updatedSocials);
        queryClient.invalidateQueries(['me']);
        setShowLinkModal(false);
        setUrlError('');
        setAlert({ show: true, message: `${selectedSocial} link removed!`, type: 'success' });
      },
      onError: (err) => {
        console.error('Remove link error:', err);
        setAlert({ show: true, message: 'Failed to remove link', type: 'error' });
      }
    });
  };

  const handleVisitLink = () => {
    const url = socialLinks[selectedSocial];
    if (url) {
      window.open(url, '_blank');
    }
  };

  const MetricRow = ({ label, value, showTime = false, timeValue }) => (
    <div className="flex items-center justify-between py-1">
      <span className="text-gray-400 dark:text-gray-400 font-normal text-[14px]">{label}</span>
      <div className="flex items-center gap-3">
        {!showTime && (
          <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-500 rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${animatedValues[label.toLowerCase().split(' ')[0]]}%` }}
            />
          </div>
        )}
        <span className="text-gray-800 dark:text-gray-300 text-[14px] font-medium min-w-[60px] text-right">
          {showTime ? (timeValue ? `${timeValue}h` : 'N/A') : `${value}%`}
        </span>
      </div>
    </div>
  );

  const SocialIcon = ({ icon: Icon, type, color }) => (
    <button
      onClick={() => handleSocialClick(type)}
      className={`relative w-12 h-12 rounded-full ${color} flex items-center justify-center text-white transition-all duration-300 hover:scale-110 hover:shadow-lg group`}
    >
      <Icon size={20} />
      <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110">
        <span className="text-white text-xs font-bold">+</span>
      </div>
      {socialLinks[type] && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
      )}
    </button>
  );

  return (
    <div className="w-full lg:w-md mx-auto bg-white dark:bg-[#1d2026] rounded-lg shadow-lg overflow-hidden">
      {alert.show && (
        <CustomAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert({ ...alert, show: false })}
        />
      )}
      {/* Header */}
      <div className="p-3 bg-gray-50 dark:bg-[#191c22]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden ring-2 ring-white dark:ring-gray-800">
              <img 
                src={user?.profilePicture || "/api/placeholder/48/48"} 
                alt="Profile" 
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="font-medium md:font-semibold text-gray-800 dark:text-white text-[14px] md:text-[16px]">{user?.firstName || 'User'} {user?.lastName || 'User'}</h2>
              <p className="text-gray-500 dark:text-gray-400 text-[10px] md:text-sm">@{user?.username || 'user'}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 bg-blue-500 text-white px-1.5 py-0.5 rounded-md shadow-sm">
            <span className="font-normal">{metrics?.avgRating || 0}</span>
            <Star size={12} fill="currentColor" />
          </div>
        </div>

        {/* Bio & Skills Section */}
        <div className="mb-3 p-4 bg-white/50 dark:bg-[#1d2026]/50 rounded-md border border-gray-100 dark:border-[#191c22]/[50] backdrop-blur-sm relative group">
          <button 
            onClick={() => setIsEditModalOpen(true)}
            className="absolute top-3 right-3 p-1.5 text-gray-400 dark:text-gray-500 hover:text-blue-500 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-all opacity-0 group-hover:opacity-100"
          >
            <Pencil size={14} />
          </button>
          
          <div className="space-y-3">
            <div>
              <h4 className="text-xs font-medium md:font-semibold text-gray-800 dark:text-gray-400 uppercase tracking-wider mb-1">Professional Bio</h4>
              <p className="text-[12px] md:text-[14px] text-gray-500 dark:text-gray-300 leading-relaxed italic">
                {user?.bio || "No professional bio added yet. Click the edit icon to share your story!"}
              </p>
            </div>
            
            <div>
              <h4 className="text-xs font-medium md:font-semibold text-gray-800 dark:text-gray-400 uppercase tracking-wider mb-2">Technical Skills</h4>
              <div className="flex flex-wrap gap-1.5">
                {user?.skills && user.skills.length > 0 ? (
                  user.skills.map((skill, index) => (
                    <span key={index} className="px-2 py-0.5 bg-gray-50 dark:bg-[#0b0e14]/30 text-gray-500 dark:text-gray-400 text-[11px] font-medium rounded-full border border-gray-100 dark:border-gray-800">
                      {skill}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-gray-400 dark:text-gray-500 italic">No skills listed</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Metrics */}
        <div className="space-y-1">
          <MetricRow label="Inbox response rate" value={metrics?.inbox || 0} />
          <MetricRow label="Inbox response time" showTime={true} timeValue={metrics?.responseTime} />
          <MetricRow label="Order response rate" value={metrics?.order || 0} />
          <MetricRow label="Delivered on time" value={metrics?.delivered || 0} />
          <MetricRow label="Order completion" value={metrics?.completion || 0} />
        </div>

        {/* Earnings */}
        <div className="mt-3 bg-green-100 dark:bg-green-900/20 rounded-md p-4 text-center">
          <p className="text-gray-700 dark:text-gray-300 font-normal md:font-medium text-[14px] md:text-[16px]">
            Total Earnings <span className="text-gray-500 dark:text-gray-400">(All Time)</span> = <span className="text-green-300 dark:text-green-400 font-medium md:font-semibold">${metrics?.totalEarnings || 0}</span>
          </p>
        </div>
      </div>

      {/* Inbox Section */}
      <div className="p-3">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-gray-800 dark:text-white text-[14px] md:text-[16px] font-medium font-semibold">Inbox</h3>
          <a href="/notifications" className="text-blue-500 font-normal text-[12px] md:text-[14px] hover:text-blue-300 transition-colors">
            View All
          </a>
        </div>

        {/* Social Networks */}
        <div className="mt-3">
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-gray-800 dark:text-white text-[12px] md:text-[14px] font-medium md:font-semibold">Link your social networks</h4>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <SocialIcon icon={Facebook} type="facebook" color="bg-blue-300" />
            <SocialIcon icon={Twitter} type="twitter" color="bg-blue-400" />
            <SocialIcon icon={Instagram} type="instagram" color="bg-pink-500" />
            <SocialIcon icon={Linkedin} type="linkedin" color="bg-blue-700" />
            <SocialIcon icon={Youtube} type="youtube" color="bg-red-500" />
            <SocialIcon icon={Github} type="github" color="bg-gray-800" />
          </div>
        </div>
      </div>

      {/* Modal for adding/editing social links */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-[#1d2026] rounded-md p-3 w-80 mx-4 animate-in fade-in zoom-in duration-200">
            <h3 className="text-lg font-semibold dark:text-white mb-4 capitalize">
              {socialLinks[selectedSocial] ? 'Edit' : 'Add'} {selectedSocial} Link
            </h3>
            
            <div className="mb-4">
              <input
                id="social-url"
                type="text"
                placeholder={`Enter your ${selectedSocial} URL`}
                className={`w-full p-3 border dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  urlError 
                    ? 'border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 dark:border-gray-300 focus:ring-blue-500'
                }`}
                defaultValue={socialLinks[selectedSocial]}
              />
              {urlError && (
                <p className="text-red-500 text-sm mt-2">{urlError}</p>
              )}
              <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
                Example: https://www.{selectedSocial}.com/username
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowLinkModal(false);
                    setUrlError('');
                  }}
                  className="flex-1 py-2 px-4 border border-gray-300 dark:border-gray-300 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveLink}
                  className="flex-1 py-2 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-300 transition-colors"
                >
                  Save
                </button>
              </div>
              
              {socialLinks[selectedSocial] && (
                <div className="flex gap-3">
                  <button
                    onClick={handleVisitLink}
                    className="flex-1 py-2 px-4 bg-green-500 text-white rounded-lg hover:bg-green-300 transition-colors"
                  >
                    Visit Link
                  </button>
                  <button
                    onClick={handleRemoveLink}
                    className="flex-1 py-2 px-4 bg-red-500 text-white rounded-lg hover:bg-red-300 transition-colors"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-[#191c22] rounded-lg p-3 w-full max-w-sm shadow-xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-[14px] text-lg font-semibold text-gray-800 dark:text-white">Edit Professional Info</h3>
              <button 
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-300 dark:hover:text-gray-300 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-[12px] font-medium text-gray-700 dark:text-gray-300 mb-1.5">Professional Bio</label>
                <textarea
                  value={editBio}
                  onChange={(e) => setEditBio(e.target.value)}
                  placeholder="Describe your expertise and experience..."
                  className="w-full p-3 border border-gray-200 dark:border-[#32353c] dark:bg-inherit dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-32 text-[12px] font-light resize-none transition-all"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Skills (comma separated)</label>
                <input
                  type="text"
                  value={editSkills}
                  onChange={(e) => setEditSkills(e.target.value)}
                  placeholder="UI Design, React, Node.js..."
                  className="w-full p-3 border border-gray-200 dark:border-[#32353c] text-[12px] font-light dark:bg-inherit dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="flex-1 py-1.5 px-2 border border-gray-200 dark:border-gray-300 rounded-md text-[12px] font-normal text-gray-300 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={updateProfile.isLoading}
                className="flex-1 py-1.5 px-2 bg-blue-500 text-white rounded-md text-[12px] font-normal hover:bg-blue-700 transition-all shadow-md shadow-blue-200 dark:shadow-blue-900/20 disabled:opacity-50"
              >
                {updateProfile.isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDashboard;